import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ListChecks, GripVertical, ChevronDown, ChevronUp } from 'lucide-react';
import { capitalizeFirst } from '@/lib/utils';

/**
 * ActivitiesStepShell
 * Reusable layout shell for Step 3 (Activities/Topics)
 * 
 * Props:
 * - topics: array - list of topic objects {id, title, activities}
 * - goals: array - course goals (for calculating global goal index)
 * - headerTitle: string - main header title
 * - headerSubtitle: string - header subtitle text
 * - onEdit: function - handler for inline editing (path, value)
 * - children: React node - additional controls (generate button, etc.)
 */
export default function ActivitiesStepShell({ 
  topics = [], 
  goals = [],
  headerTitle,
  headerSubtitle,
  onEdit = null,
  children 
}) {
  const { t } = useTranslation();
  const defaultHeaderTitle = headerTitle || t('step3.designActivities');
  const defaultHeaderSubtitle = headerSubtitle || t('step3.createStructured');
  const [expandedTopics, setExpandedTopics] = useState(new Set());
  const hasActivities = topics && topics.length > 0;

  // Keep expanded topics stable across topic updates (e.g., tool edits)
  // while cleaning up IDs that no longer exist.
  useEffect(() => {
    if (!topics) return;
    setExpandedTopics(prev => {
      if (!prev || prev.size === 0) return new Set(); // initial state stays collapsed
      const validIds = new Set(topics.map(t => t.id));
      return new Set([...prev].filter(id => validIds.has(id)));
    });
  }, [topics]);

  const toggleTopic = (topicId) => {
    setExpandedTopics(prev => {
      const next = new Set(prev);
      if (next.has(topicId)) {
        next.delete(topicId);
      } else {
        next.add(topicId);
      }
      return next;
    });
  };

  return (
    <div className="w-full" data-testid="activities-step-shell">
      <div className="flex-1 p-8 overflow-y-auto">
        <div className="max-w-4xl mx-auto">
          <div className="space-y-6">
            {/* Topics/Activities list */}
            {hasActivities && (
              <div className="space-y-4" data-testid="topics-list">
                {topics.map((topic, topicIndex) => {
                  const isExpanded = expandedTopics.has(topic.id);
                  return (
                    <div 
                      key={topic.id} 
                      className="bg-white p-6 rounded-2xl shadow-xl border-2 border-pink-100" 
                      data-testid={`topic-${topicIndex}`}
                    >
                      <div 
                        className="flex items-start gap-3 mb-4 border border-pink-200 rounded-xl px-4 py-3 bg-pink-50/60 cursor-pointer hover:bg-pink-100/60 transition-colors"
                        onClick={() => toggleTopic(topic.id)}
                      >
                        <GripVertical className="mt-1 text-gray-300" size={18} />
                        <div className="flex-1">
                          <h3 
                            className="text-2xl font-bold text-gray-900 hover:bg-white/50 px-2 py-1 rounded transition-colors cursor-text" 
                            data-testid={`topic-title-${topicIndex}`}
                            contentEditable={!!onEdit}
                            suppressContentEditableWarning
                            onClick={(e) => e.stopPropagation()}
                            onBlur={onEdit ? (e) => onEdit(`topics.${topicIndex}.title`, e.target.textContent) : undefined}
                          >
                            {topic.title}
                          </h3>
                        </div>
                        <div className="rounded-full bg-pink-100 text-pink-700 text-xs font-semibold px-3 py-1">
                          {topic.activities?.length || 0} {t('step4.activities')}
                        </div>
                        {isExpanded ? (
                          <ChevronUp className="text-pink-500" size={24} />
                        ) : (
                          <ChevronDown className="text-pink-500" size={24} />
                        )}
                      </div>

                      {isExpanded && (
                        <div className="space-y-3 ml-8 border-l-4 border-pink-100 pl-6">
                          {topic.activities?.map((activity, actIndex) => (
                            <div 
                              key={activity.id} 
                              className="p-5 bg-gradient-to-r from-pink-50 to-teal-50 rounded-xl space-y-3" 
                              data-testid={`activity-${topicIndex}-${actIndex}`}
                            >
                              <div className="rounded-xl border border-pink-200/70 bg-white/70 px-4 py-3 shadow-sm">
                                <p className="text-xs uppercase tracking-wide text-pink-500 font-semibold mb-1">{t('step3.activityTitle')}</p>
                                <h4 
                                  className="text-lg font-semibold text-gray-900 hover:bg-pink-50 px-2 py-1 rounded transition-colors cursor-text" 
                                  data-testid={`activity-title-${topicIndex}-${actIndex}`}
                                  contentEditable={!!onEdit}
                                  suppressContentEditableWarning
                                  onBlur={onEdit ? (e) => onEdit(`topics.${topicIndex}.activities.${actIndex}.title`, e.target.textContent) : undefined}
                                >
                                  {activity.title}
                                </h4>
                              </div>

                              <div className="rounded-xl border border-teal-200/70 bg-white/70 px-4 py-3 shadow-sm">
                                <p className="text-xs uppercase tracking-wide text-teal-500 font-semibold mb-1">{t('step3.description')}</p>
                                <p 
                                  className="text-sm text-gray-700 leading-relaxed max-h-32 overflow-y-auto hover:bg-teal-50 px-2 py-1 rounded transition-colors cursor-text" 
                                  data-testid={`activity-description-${topicIndex}-${actIndex}`}
                                  contentEditable={!!onEdit}
                                  suppressContentEditableWarning
                                  onBlur={onEdit ? (e) => onEdit(`topics.${topicIndex}.activities.${actIndex}.description`, e.target.textContent) : undefined}
                                >
                                  {activity.description}
                                </p>
                              </div>

                              {(activity.actor || activity.bloomLevel || activity.facilitatorRole) && (
                                <div className="flex flex-wrap items-center gap-3 text-xs font-semibold text-gray-500">
                                  {activity.actor && (
                                    <span className="px-3 py-1 rounded-full bg-gray-100 text-gray-700 border border-gray-200">
                                      <span className="text-gray-500">{t('step3.actor')}: </span>
                                      <span
                                        className="hover:bg-gray-200 px-1 rounded cursor-text"
                                        contentEditable={!!onEdit}
                                        suppressContentEditableWarning
                                        onBlur={onEdit ? (e) => onEdit(`topics.${topicIndex}.activities.${actIndex}.actor`, e.target.textContent) : undefined}
                                      >
                                        {capitalizeFirst(activity.actor)}
                                      </span>
                                    </span>
                                  )}
                                  {activity.bloomLevel && (
                                    <span className="px-3 py-1 rounded-full bg-gray-100 text-gray-700 border border-gray-200">
                                      <span className="text-gray-500">{t('step3.bloom')}: </span>
                                      <span
                                        className="hover:bg-gray-200 px-1 rounded cursor-text"
                                        contentEditable={!!onEdit}
                                        suppressContentEditableWarning
                                        onBlur={onEdit ? (e) => onEdit(`topics.${topicIndex}.activities.${actIndex}.bloomLevel`, e.target.textContent) : undefined}
                                      >
                                        {activity.bloomLevel}
                                      </span>
                                    </span>
                                  )}
                                  {activity.facilitatorRole && (
                                    <span className="px-3 py-1 rounded-full bg-gray-100 text-gray-700 border border-gray-200">
                                      <span className="text-gray-500">{t('step3.facilitator')}: </span>
                                      <span
                                        className="hover:bg-gray-200 px-1 rounded cursor-text"
                                        contentEditable={!!onEdit}
                                        suppressContentEditableWarning
                                        onBlur={onEdit ? (e) => onEdit(`topics.${topicIndex}.activities.${actIndex}.facilitatorRole`, e.target.textContent) : undefined}
                                      >
                                        {activity.facilitatorRole}
                                      </span>
                                    </span>
                                  )}
                                </div>
                              )}

                              {(activity.resourceType || activity.resourceTitle || activity.resourceDescription) && (
                                <div className="rounded-xl border border-emerald-200/70 bg-white/70 px-4 py-3 shadow-sm space-y-2">
                                  <p className="text-xs uppercase tracking-wide text-emerald-500 font-semibold">{t('step3.resource')}</p>
                                  {activity.resourceType && (
                                    <p className="text-sm text-gray-700">
                                      <span className="font-semibold text-xs uppercase tracking-wide text-emerald-500 mr-2">{t('step3.type')}:</span>
                                      <span 
                                        className="hover:bg-emerald-50 px-1 rounded cursor-text"
                                        contentEditable={!!onEdit}
                                        suppressContentEditableWarning
                                        onBlur={onEdit ? (e) => onEdit(`topics.${topicIndex}.activities.${actIndex}.resourceType`, e.target.textContent) : undefined}
                                      >
                                        {activity.resourceType}
                                      </span>
                                    </p>
                                  )}
                                  {activity.resourceTitle && (
                                    <p className="text-sm text-gray-700">
                                      <span className="font-semibold text-xs uppercase tracking-wide text-emerald-500 mr-2">{t('step1.title')}:</span>
                                      <span 
                                        className="hover:bg-emerald-50 px-1 rounded cursor-text"
                                        contentEditable={!!onEdit}
                                        suppressContentEditableWarning
                                        onBlur={onEdit ? (e) => onEdit(`topics.${topicIndex}.activities.${actIndex}.resourceTitle`, e.target.textContent) : undefined}
                                      >
                                        {activity.resourceTitle}
                                      </span>
                                    </p>
                                  )}
                                  {activity.resourceDescription && (
                                    <p 
                                      className="text-sm text-gray-700 leading-relaxed max-h-24 overflow-y-auto hover:bg-emerald-50 px-2 py-1 rounded cursor-text"
                                      contentEditable={!!onEdit}
                                      suppressContentEditableWarning
                                      onBlur={onEdit ? (e) => onEdit(`topics.${topicIndex}.activities.${actIndex}.resourceDescription`, e.target.textContent) : undefined}
                                    >
                                      {activity.resourceDescription}
                                    </p>
                                  )}
                                </div>
                              )}

                              {(() => {
                                // Build objective entries from objectiveIds/objectiveId (or legacy goalIds/goalId)
                                // Support both new and legacy field names for backward compatibility
                                const objectiveIds =
                                  (activity.objectiveIds && activity.objectiveIds.length > 0)
                                    ? activity.objectiveIds
                                    : (activity.goalIds && activity.goalIds.length > 0)
                                    ? activity.goalIds
                                    : (activity.objectiveId || activity.goalId)
                                    ? [activity.objectiveId || activity.goalId]
                                    : [];

                                const goalEntries = objectiveIds
                                  .map((gid, idx) => {
                                    const goalObj = goals?.find(g => g.id === gid);
                                    const text =
                                      goalObj?.text ||
                                      (activity.goals && activity.goals[idx]) ||
                                      '';
                                    if (!text) return null;
                                    const globalIdx =
                                      goals && goals.length > 0
                                        ? goals.findIndex(g => g.id === gid)
                                        : -1;
                                    return { text, globalIdx };
                                  })
                                  .filter(Boolean);

                                // If no objectiveIds but activity.goals exists (text-based), render those
                                if ((!goalEntries || goalEntries.length === 0) && activity.goals?.length) {
                                  activity.goals.forEach((t, idx) => {
                                    goalEntries.push({ text: t, globalIdx: -1 });
                                  });
                                }

                                if (!goalEntries || goalEntries.length === 0) return null;

                                return (
                                  <div className="rounded-xl border border-orange-200/70 bg-white/70 px-4 py-3 shadow-sm space-y-2">
                                    <p className="text-xs uppercase tracking-wide text-orange-500 font-semibold">{t('step3.alignedObjectives')}</p>
                                    <div className="space-y-2">
                                      {goalEntries.map((entry, idx) => (
                                        <div key={idx} className="flex flex-col gap-1">
                                          <span className="text-[11px] font-semibold text-orange-600 uppercase tracking-wide">
                                            L.O. {entry.globalIdx >= 0 ? entry.globalIdx + 1 : idx + 1}
                                          </span>
                                          <p className="text-sm text-gray-700 leading-snug">
                                            {entry.text}
                                          </p>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                );
                              })()}

                              {activity.duration && (
                                <div className="flex items-center gap-2 text-xs text-gray-500">
                                  <span className="font-semibold">{t('step3.duration')}:</span>
                                  <span>{activity.duration} {t('step3.min')}</span>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Additional controls (generate button, chat, etc.) */}
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}


